import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [error, setError] = useState(null);

  // Configurar api com token
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Verificar se usuário está autenticado
  useEffect(() => {
    // Só mostrar spinner se não há usuário em memória ainda (cold start)
    // Re-validações com usuário já presente são silenciosas (sem spinner)
    const isColdStart = !user;
    if (isColdStart) setLoading(true);

    const checkAuth = async () => {
      if (token) {
        try {
          // Fazer uma requisição para verificar se o token é válido
          const response = await api.get('/auth/me');
          setUser(response.data.data.user);
        } catch (error) {
          console.error('Erro ao verificar autenticação:', error);
          logout();
        }
      } else {
        // Se não há token, não fazer requisições
        setUser(null);
      }
      setLoading(false);
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const login = async (email, password) => {
    try {
      setError(null);
      const response = await api.post('/auth/login', {
        email,
        password
      });

      const { token: newToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));

      return { success: true };
    } catch (error) {
      console.error('Erro no login:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao fazer login';
      const contactInfo = error.response?.data?.contactInfo || null;
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
        contactInfo
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      setError(null);
      const response = await api.post('/auth/register', {
        name,
        email,
        password
      });

      const { token: newToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));

      return true;
    } catch (error) {
      console.error('Erro no registro:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao criar conta';
      setError(errorMessage);
      return false;
    }
  };

  // Login com Google
  const loginWithGoogle = async (credential, tokenType = 'id_token') => {
    try {
      setError(null);
      const payload = tokenType === 'access_token'
        ? { access_token: credential, tokenType }
        : { credential };
      const response = await api.post('/auth/google', payload);

      const { token: newToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));

      return { success: true };
    } catch (error) {
      console.error('Erro no login com Google:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao fazer login com Google';
      const contactInfo = error.response?.data?.contactInfo || null;
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage,
        contactInfo
      };
    }
  };

  const forgotPassword = async (email) => {
    try {
      setError(null);
      const response = await api.post('/auth/forgotpassword', { email });
      return { success: true, message: 'Email de redefinição enviado' };
    } catch (error) {
      console.error('Erro ao solicitar redefinição:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao enviar email de redefinição';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const resetPassword = async (token, password) => {
    try {
      setError(null);
      const response = await api.put(`/auth/resetpassword/${token}`, { password });

      const { token: newToken, user: userData } = response.data;

      setToken(newToken);
      setUser(userData);
      localStorage.setItem('token', newToken);
      localStorage.setItem('user', JSON.stringify(userData));

      return { success: true, message: 'Senha redefinida com sucesso' };
    } catch (error) {
      console.error('Erro ao redefinir senha:', error);
      const errorMessage = error.response?.data?.message || 'Erro ao redefinir senha';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setError(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isMensure = () => {
    return user?.role === 'mensure';
  };

  const isAdminOrMensure = () => {
    return user?.role === 'admin' || user?.role === 'mensure';
  };

  const canManageDomains = () => {
    return isAdminOrMensure() || user?.permissions?.canManageDomains;
  };

  const canAccessAdminPanel = () => {
    return isAdminOrMensure() || user?.permissions?.canAccessAdminPanel;
  };

  const updateUser = (userData) => {
    setUser(userData);
    localStorage.setItem('user', JSON.stringify(userData));
  };

  const value = {
    user,
    token,
    loading,
    error,
    login,
    loginWithGoogle,
    register,
    forgotPassword,
    resetPassword,
    logout,
    updateUser,
    isAdmin,
    isMensure,
    isAdminOrMensure,
    canManageDomains,
    canAccessAdminPanel
  };


  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;